import React from 'react';
import styled from 'styled-components';
import { useAccount } from 'wagmi';
import { WLFI_COLORS } from './WLFILayout';
import { Wallet, TrendingUp, History, Star } from 'lucide-react';
import PortfolioPreview from '../../components/discover/PortfolioPreview';

// ============================================
// WLFI PORTFOLIO PAGE
// Shows user's USD1 token holdings and activity
// ============================================

const PageContainer = styled.div`
  padding: 0;
  width: 100%;
`;

const PageHeader = styled.div`
  margin-bottom: 32px;
`;

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 8px 0;
`;

const PageSubtitle = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: ${WLFI_COLORS.cardBackground};
  border: 1px solid ${WLFI_COLORS.goldBorder};
  border-radius: 16px;
  padding: 24px;
`;

const CardTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 10px;
  
  svg {
    color: ${WLFI_COLORS.gold};
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  text-align: center;
  color: rgba(255, 255, 255, 0.4);
  
  svg {
    width: 48px;
    height: 48px;
    margin-bottom: 16px;
    opacity: 0.3;
  }
  
  h3 {
    font-size: 16px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.6);
    margin: 0 0 8px 0;
  }
  
  p {
    font-size: 13px;
    margin: 0;
  }
`;

const ConnectPrompt = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  background: ${WLFI_COLORS.cardBackground};
  border: 1px solid ${WLFI_COLORS.goldBorder};
  border-radius: 16px;
  
  svg {
    width: 64px;
    height: 64px;
    color: ${WLFI_COLORS.gold};
    margin-bottom: 24px;
    opacity: 0.6;
  }
  
  h2 {
    font-size: 22px;
    font-weight: 700;
    color: #ffffff;
    margin: 0 0 12px 0;
  }
  
  p {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.5);
    margin: 0;
    max-width: 400px;
  }
`;

const PortfolioWrapper = styled.div`
  /* Override default styles for WLFI theme */
  & > div {
    background: transparent;
    border: none;
    padding: 0;
  }
`;

const WLFIPortfolioPage: React.FC = () => {
  const { isConnected, address } = useAccount();
  
  if (!isConnected) {
    return (
      <PageContainer>
        <ConnectPrompt>
          <Wallet />
          <h2>Connect Your Wallet</h2>
          <p>
            Connect your wallet to view your USD1 token holdings, 
            transaction history, and favorited tokens.
          </p>
        </ConnectPrompt>
      </PageContainer>
    );
  }
  
  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Portfolio</PageTitle>
        <PageSubtitle>
          Your USD1-paired token holdings on Ethereum
        </PageSubtitle>
      </PageHeader>
      
      <ContentGrid>
        {/* Holdings */}
        <Card>
          <CardTitle>
            <Wallet size={20} /> Holdings
          </CardTitle>
          <PortfolioWrapper>
            <PortfolioPreview />
          </PortfolioWrapper>
        </Card>
        
        {/* Favorites */}
        <Card>
          <CardTitle>
            <Star size={20} /> Favorited Tokens
          </CardTitle>
          <EmptyState>
            <Star />
            <h3>No favorites yet</h3>
            <p>Favorite tokens from the Explore page to track them here.</p>
          </EmptyState>
        </Card>
        
        {/* Recent Activity */}
        <Card style={{ gridColumn: '1 / -1' }}>
          <CardTitle>
            <History size={20} /> Recent Activity
          </CardTitle>
          <EmptyState>
            <History />
            <h3>No recent activity</h3>
            <p>Your token deployments and trades will appear here.</p>
          </EmptyState>
        </Card>
      </ContentGrid>
    </PageContainer>
  );
};

export default WLFIPortfolioPage;

